import React from 'react';
import { View, ViewStyle, Dimensions } from 'react-native';
import { useTheme } from 'react-native-paper';
import { useNavigation } from '@react-navigation/native';
import { ResponsiveLayoutWrapper } from './ResponsiveLayoutWrapper';
import { SidebarNavigation } from './SidebarNavigation';
import type { AppNavigationProp } from '../../navigation/types';

interface ResponsiveLayoutProps {
  children: React.ReactNode;
  activeRoute?: string;
  onNavigate?: (route: string) => void;
  showSidebar?: boolean;
  style?: ViewStyle;
}

/**
 * ResponsiveLayout - Main layout component that adapts to screen size
 *
 * Breakpoint: 768px (matches navigationConfig.breakpoints.mobile)
 * - Mobile (< 768px): Full-width content with bottom tabs
 * - Desktop (>= 768px): Sidebar + centered content with max-width 700px
 * - Responsive to orientation changes
 * - Maintains proper content flow across screen sizes
 */
export function ResponsiveLayout({
  children,
  activeRoute = 'Home',
  onNavigate = () => {},
  showSidebar = true,
  style,
}: ResponsiveLayoutProps) {
  const theme = useTheme();
  const navigation = useNavigation<AppNavigationProp>();
  const [isLargeScreen, setIsLargeScreen] = React.useState(false);

  React.useEffect(() => {
    const updateScreenSize = () => {
      const { width } = Dimensions.get('window');
      const isLarge = width >= 768;
      // Use >= 768 to match MainNavigator's breakpoint logic
      setIsLargeScreen(isLarge);
    };

    updateScreenSize(); // Set initial value

    const subscription = Dimensions.addEventListener(
      'change',
      updateScreenSize
    );
    return () => subscription?.remove();
  }, [showSidebar]);

  // Mobile layout (< 768px) - full-width content
  const renderMobileLayout = () => (
    <View style={[{ flex: 1 }, style]}>{children}</View>
  );

  // Handle navigation from sidebar/header
  const handleSidebarNavigate = React.useCallback(
    (route: string) => {
      // For large screens with stack navigator, navigate directly
      // For small screens with drawer navigator, use drawer navigation
      if (isLargeScreen) {
        // Large screens use Stack Navigator - navigate directly
        (navigation as any).navigate?.(route);
      } else {
        // Small screens use Drawer Navigator - navigate within drawer
        const drawerNavigator = (navigation as any).getParent?.();
        if (drawerNavigator?.navigate) {
          drawerNavigator.navigate(route);
        } else {
          // Fallback: try direct navigation
          (navigation as any).navigate?.(route);
        }
      }
      onNavigate(route);
    },
    [navigation, onNavigate, isLargeScreen]
  );

  // Desktop layout (≥ 768px) - sidebar + centered content
  const renderDesktopLayout = () => {
    return (
      <View style={[styles.desktopContainer, style]}>
        {/* Sidebar */}
        {showSidebar && (
          <SidebarNavigation
            activeRoute={activeRoute}
            onNavigate={handleSidebarNavigate}
          />
        )}

        {/* Main content area */}
        <View style={styles.mainContent}>
          <ResponsiveLayoutWrapper>{children}</ResponsiveLayoutWrapper>
        </View>
      </View>
    );
  };

  return isLargeScreen ? renderDesktopLayout() : renderMobileLayout();
}

const styles = {
  desktopContainer: {
    flex: 1,
    flexDirection: 'row' as const,
  },
  mainContent: {
    flex: 1,
    backgroundColor: 'transparent',
  },
};

export default ResponsiveLayout;
